import { useEffect, useState } from 'react';
import { matrimonyAPI } from '../utils/api';

/**
 * DynamicFormSection - Renders a form section based on backend configuration
 * 
 * Props:
 *   sectionConfig - Section config object with title, icon, and fields array
 *   form - Current form state object
 *   upd - Function to update form state: upd(fieldName) => (e) => {...}
 */
function DynamicFormSection({ sectionConfig, form, upd }) {
  if (!sectionConfig || !sectionConfig.fields) return null;

  // Filter fields: only show if no conditional or if conditional matches
  const visibleFields = sectionConfig.fields.filter(field => {
    if (!field.conditional) return true;

    const conditionalField = field.conditional;
    const conditionalValue = form[conditionalField.field];
    const shouldShow = conditionalValue === conditionalField.value;

    // no verbose logging in production
    return shouldShow;
  });

  return (
    <div className="card" style={{ marginBottom: 'clamp(0.8rem, 2vw, 1.2rem)' }}>
      <div style={{
        fontFamily: 'var(--serif)',
        fontSize: 'clamp(1rem, 2.5vw, 1.15rem)',
        fontWeight: 600,
        marginBottom: 'clamp(0.7rem, 2vw, 1.1rem)',
        paddingBottom: 'clamp(0.4rem, 1vw, 0.65rem)',
        borderBottom: '1px solid var(--cream-d)'
      }}>
        {sectionConfig.icon} {sectionConfig.title}
      </div>

      <div className={`g${visibleFields.filter(f => !f.fullWidth).length <= 1 ? '' : '2'}`}>
        {visibleFields.map(field => (
          <FormField
            key={field.name}
            field={field}
            value={form[field.name] || ''}
            onChange={upd(field.name)}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * FormField - Renders a single form field based on its type
 */
function FormField({ field, value, onChange }) {
  const fieldStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: 'clamp(0.3rem, 1vw, 0.5rem)',
    ...(field.fullWidth && { gridColumn: '1 / -1' })
  };

  const labelStyle = {
    fontSize: 'clamp(0.85rem, 2vw, 0.95rem)',
    fontWeight: 500
  };

  const inputStyle = {
    fontSize: 'clamp(0.8rem, 1.8vw, 0.95rem)',
    padding: 'clamp(0.5rem, 1.5vw, 0.75rem)',
    border: '1px solid var(--cream-d)',
    borderRadius: 'var(--r)',
    fontFamily: 'inherit'
  };

  return (
    <div style={fieldStyle}>
      <label style={labelStyle}>
        {field.label}
        {field.required && <span style={{ color: 'var(--spice)' }}>*</span>}
      </label>

      {field.type === 'text' && (
        <input
          type="text"
          value={value}
          onChange={onChange}
          placeholder={field.placeholder || ''}
          style={inputStyle}
          required={field.required}
        />
      )}

      {field.type === 'email' && (
        <input
          type="email"
          value={value}
          onChange={onChange}
          placeholder={field.placeholder || ''}
          style={inputStyle}
          required={field.required}
        />
      )}

      {field.type === 'tel' && (
        <input
          type="tel"
          value={value}
          onChange={onChange}
          placeholder={field.placeholder || ''}
          style={inputStyle}
          required={field.required}
        />
      )}

      {field.type === 'number' && (
        <input
          type="number"
          value={value}
          onChange={onChange}
          placeholder={field.placeholder || ''}
          style={inputStyle}
          required={field.required}
        />
      )}

      {field.type === 'date' && (
        <input
          type="date"
          value={value}
          onChange={onChange}
          style={inputStyle}
          required={field.required}
        />
      )}

      {field.type === 'time' && (
        <input
          type="time"
          value={value}
          onChange={onChange}
          style={inputStyle}
          required={field.required}
        />
      )}

      {field.type === 'select' && (
        <select
          value={value}
          onChange={onChange}
          style={inputStyle}
          required={field.required}
        >
          <option value="">-- Select {field.label.toLowerCase()} --</option>
          {field.options && field.options.map(opt => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      )}

      {field.type === 'textarea' && (
        <textarea
          value={value}
          onChange={onChange}
          placeholder={field.placeholder || ''}
          style={{
            ...inputStyle,
            minHeight: 'clamp(80px, 15vw, 120px)',
            fontFamily: 'inherit',
            resize: 'vertical'
          }}
          required={field.required}
        />
      )}
    </div>
  );
}

/**
 * DynamicForm - Main component that loads form config from backend and renders all sections
 * 
 * Props:
 *   form - Current form state object
 *   upd - Function to update form state: upd(fieldName) => (e) => {...}
 *   loading - Boolean indicating if form is loading
 */
export function DynamicForm({ form, upd, loading = false }) {
  const [formConfig, setFormConfig] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
  // Fetch form configuration from backend
    matrimonyAPI.getProfileFormConfig()
      .then(config => {
        // form config loaded
        setFormConfig(config);
        setError(null);
      })
      .catch(err => {
        console.error('✗ Failed to load form config:', err);
        // Try direct fetch as fallback
        // retrying with direct fetch
        fetch('/api/matrimony/profile-form-config/')
          .then(r => r.json())
          .then(config => {
            // form config loaded (direct fetch)
            setFormConfig(config);
            setError(null);
          })
          .catch(fallbackErr => {
            console.error('✗ Direct fetch also failed:', fallbackErr);
            const errorMsg = err?.detail || err?.message || 'Could not load form configuration. Please refresh the page.';
            setError(errorMsg);
          });
      });
  }, []);

  // Removed verbose form-state debug logging

  if (error) {
    return (
      <div style={{
        background: 'var(--rose-l)',
        border: '1px solid var(--rose)',
        color: 'var(--rose-d)',
        padding: 'clamp(0.75rem, 2vw, 1rem)',
        borderRadius: 'var(--r)',
        marginBottom: 'clamp(0.8rem, 2vw, 1.2rem)'
      }}>
        {error}
      </div>
    );
  }

  if (!formConfig) {
    return (
      <div style={{
        textAlign: 'center',
        padding: 'clamp(1rem, 3vw, 2rem)',
        color: 'var(--mid)'
      }}>
        Loading form...
      </div>
    );
  }

  return (
    <>
      {Object.entries(formConfig).map(([sectionKey, sectionConfig]) => (
        <DynamicFormSection
          key={sectionKey}
          sectionConfig={sectionConfig}
          form={form}
          upd={upd}
        />
      ))}
    </>
  );
}

export default DynamicForm;
